<template>
  <div class="peninsula-calendar">
    <div v-if="!startDateField" class="peninsula-calendar__empty">
      <i class="peninsula-calendar__empty-icon iconoir-calendar" />
      <h2>Select a start field</h2>
      <p>Open Calendar fields and select Date or Job schedule.</p>
    </div>

    <ClientOnly v-else>
      <FullCalendar ref="calendar" :options="calendarOptions">
        <template #eventContent="arg">
          <div class="peninsula-calendar__event-content">
            <span v-if="arg.timeText" class="peninsula-calendar__event-time">
              {{ arg.timeText }}
            </span>
            <strong class="peninsula-calendar__event-title">
              {{ arg.event.title }}
            </strong>
            <span
              v-for="label in arg.event.extendedProps.labels"
              :key="label"
              class="peninsula-calendar__event-label"
            >
              {{ label }}
            </span>
          </div>
        </template>
      </FullCalendar>
    </ClientOnly>

    <RowCreateModal
      v-if="canCreateRow"
      ref="rowCreateModal"
      :database="database"
      :table="table"
      :view="view"
      :visible-fields="visibleFields"
      :hidden-fields="hiddenFields"
      :show-hidden-fields="showHiddenFieldsInRowModal"
      :all-fields-in-table="fields"
      @created="createRow"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @order-fields="orderFields"
      @toggle-field-visibility="toggleFieldVisibility"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
    />

    <RowEditModal
      ref="rowEditModal"
      enable-navigation
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :visible-fields="visibleFields"
      :hidden-fields="hiddenFields"
      :rows="loadedRows"
      :read-only="!canUpdateRow"
      :show-hidden-fields="showHiddenFieldsInRowModal"
      @hidden="$emit('selected-row', undefined)"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @update="updateValue"
      @order-fields="orderFields"
      @toggle-field-visibility="toggleFieldVisibility"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="fieldCreated"
      @field-created-callback-done="afterFieldCreatedUpdateFieldOptions"
      @navigate-previous="$emit('navigate-previous', $event, activeSearchTerm)"
      @navigate-next="$emit('navigate-next', $event, activeSearchTerm)"
      @refresh-row="refreshRow"
    />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import { mapGetters } from 'vuex'

import { clone } from '@baserow/modules/core/utils/object'
import { notifyIf } from '@baserow/modules/core/utils/error'
import RowCreateModal from '@baserow/modules/database/components/row/RowCreateModal'
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import viewHelpers from '@baserow/modules/database/mixins/viewHelpers'
import { scheduleFromParts } from '@peninsula/jobSchedule'
import {
  filterHiddenFieldsFunction,
  filterVisibleFieldsFunction,
  sortFieldsByOrderAndIdFunction,
} from '@baserow/modules/database/utils/view'

const HOUR = 60 * 60 * 1000

export default {
  name: 'CalendarView',
  components: { FullCalendar, RowCreateModal, RowEditModal },
  mixins: [viewHelpers],
  props: {
    fields: { type: Array, required: true },
    view: { type: Object, required: true },
    table: { type: Object, required: true },
    database: { type: Object, required: true },
    readOnly: { type: Boolean, required: true },
    storePrefix: { type: String, required: true },
  },
  emits: ['navigate-next', 'navigate-previous', 'refresh', 'selected-row'],
  data() {
    return {
      isMobile:
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 768px)').matches,
      showHiddenFieldsInRowModal: false,
    }
  },
  computed: {
    ...mapGetters({ selectedRow: 'rowModalNavigation/getRow' }),
    storePath() {
      return `${this.storePrefix}view/peninsula_calendar`
    },
    allRows() {
      return this.$store.getters[`${this.storePath}/getRows`]
    },
    loadedRows() {
      return this.allRows.filter(Boolean)
    },
    fieldOptions() {
      return this.$store.getters[`${this.storePath}/getAllFieldOptions`]
    },
    visibleFields() {
      return this.fields
        .filter(filterVisibleFieldsFunction(this.fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(this.fieldOptions))
    },
    hiddenFields() {
      return this.fields
        .filter(filterHiddenFieldsFunction(this.fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(this.fieldOptions))
    },
    primaryField() {
      return this.fields.find((field) => field.primary) || this.fields[0]
    },
    startDateField() {
      return (
        this.fields.find((field) => field.id === this.view.start_date_field) ||
        null
      )
    },
    endDateField() {
      return (
        this.fields.find((field) => field.id === this.view.end_date_field) ||
        null
      )
    },
    usesJobSchedule() {
      return this.startDateField?.type === 'job_schedule'
    },
    colorField() {
      return (
        this.fields.find((field) => field.id === this.view.color_field) || null
      )
    },
    labelFields() {
      const excluded = new Set(
        [
          this.primaryField?.id,
          this.startDateField?.id,
          this.endDateField?.id,
          this.colorField?.id,
        ].filter(Boolean)
      )
      return this.visibleFields
        .filter((field) => !excluded.has(field.id))
        .slice(0, 2)
    },
    canCreateRow() {
      return (
        !this.readOnly &&
        !!this.startDateField &&
        (!this.table.data_sync || this.table.data_sync.two_way_sync) &&
        (this.$hasPermission(
          'database.table.create_row',
          this.table,
          this.database.workspace.id
        ) ||
          this.$hasPermission(
            'database.table.view.create_row',
            this.view,
            this.database.workspace.id
          ))
      )
    },
    canUpdateRow() {
      return (
        !this.readOnly &&
        (this.$hasPermission(
          'database.table.update_row',
          this.table,
          this.database.workspace.id
        ) ||
          this.$hasPermission(
            'database.table.view.update_row',
            this.view,
            this.database.workspace.id
          ))
      )
    },
    activeSearchTerm() {
      return this.$store.getters[`${this.storePath}/getActiveSearchTerm`]
    },
    events() {
      if (!this.startDateField) return []
      return this.loadedRows
        .filter((row) => row[`field_${this.startDateField.id}`])
        .map((row) => this.rowToEvent(row))
    },
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        locale: 'en',
        initialView: this.isMobile ? 'timeGridDay' : 'timeGridWeek',
        headerToolbar: this.isMobile
          ? {
              left: 'prev,next',
              center: 'title',
              right: 'today dayGridMonth,timeGridWeek,timeGridDay',
            }
          : {
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            },
        buttonText: {
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day',
        },
        allDayText: 'All day',
        firstDay: 1,
        nowIndicator: true,
        navLinks: true,
        selectable: this.canCreateRow,
        selectMirror: true,
        editable: this.canUpdateRow,
        eventStartEditable: this.canUpdateRow,
        eventDurationEditable:
          this.canUpdateRow && (this.usesJobSchedule || !!this.endDateField),
        longPressDelay: 300,
        eventLongPressDelay: 300,
        selectLongPressDelay: 300,
        slotDuration: '00:30:00',
        snapDuration: '00:15:00',
        slotLabelInterval: '01:00:00',
        slotMinTime: '00:00:00',
        slotMaxTime: '24:00:00',
        scrollTime: '08:00:00',
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        },
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        },
        dayMaxEvents: true,
        height: '100%',
        events: this.events,
        select: this.createFromSelection,
        eventClick: this.openEvent,
        eventDrop: this.moveEvent,
        eventResize: this.resizeEvent,
      }
    },
  },
  watch: {
    selectedRow(row, oldRow) {
      if (!this.$refs.rowEditModal) return
      if (row && (!oldRow || oldRow.id !== row.id)) {
        this.$refs.rowEditModal.show(row.id, clone(row))
      } else if (!row && oldRow) {
        this.$refs.rowEditModal.hide(false)
      }
    },
  },
  mounted() {
    this.updateBreakpoint()
    window.addEventListener('resize', this.updateBreakpoint)
    if (this.selectedRow) {
      this.$refs.rowEditModal.show(this.selectedRow.id, clone(this.selectedRow))
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateBreakpoint)
  },
  methods: {
    updateBreakpoint() {
      this.isMobile = window.matchMedia('(max-width: 768px)').matches
    },
    localDate(date) {
      const pad = (value) => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}`
    },
    valueForField(date, field) {
      return field.date_include_time ? date.toISOString() : this.localDate(date)
    },
    localTime(date) {
      const pad = (value) => String(value).padStart(2, '0')
      return `${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    valueForSchedule(start, end, allDay = false) {
      return scheduleFromParts(
        {
          date: this.localDate(start),
          startTime: allDay ? '' : this.localTime(start),
          endTime: allDay ? '' : this.localTime(end),
        },
        this.startDateField.timezone
      )
    },
    humanValue(field, row) {
      const value = row[`field_${field.id}`]
      if (value === null || value === undefined || value === '') return ''
      return this.$registry
        .get('field', field.type)
        .toHumanReadableString(field, value)
    },
    rowToEvent(row) {
      const source = row[`field_${this.startDateField.id}`]
      const start = this.usesJobSchedule ? source.start : source
      const allDay = this.usesJobSchedule
        ? source.all_day === true
        : !this.startDateField.date_include_time
      let end = this.usesJobSchedule
        ? source.end
        : this.endDateField
          ? row[`field_${this.endDateField.id}`]
          : null

      if (!end) {
        if (allDay) {
          const [year, month, day] = start.split('-').map(Number)
          end = this.localDate(new Date(year, month - 1, day + 1))
        } else {
          end = new Date(new Date(start).getTime() + HOUR).toISOString()
        }
      }

      const option = this.colorField ? row[`field_${this.colorField.id}`] : null
      const color = option?.color || null
      const title = this.primaryField
        ? this.humanValue(this.primaryField, row) || `#${row.id}`
        : `#${row.id}`

      return {
        id: String(row.id),
        title,
        start,
        end,
        allDay,
        durationEditable: !allDay,
        classNames: color ? [`background-color--${color}`] : [],
        backgroundColor: color ? undefined : '#007aff',
        borderColor: 'transparent',
        textColor: color?.includes('dark') ? '#ffffff' : '#17212b',
        extendedProps: {
          row,
          labels: this.labelFields
            .map((field) => this.humanValue(field, row))
            .filter(Boolean),
        },
      }
    },
    createFromSelection(selection) {
      if (!this.canCreateRow) return
      const defaults = {
        [`field_${this.startDateField.id}`]: this.usesJobSchedule
          ? this.valueForSchedule(
              selection.start,
              selection.end,
              selection.allDay
            )
          : this.valueForField(selection.start, this.startDateField),
      }
      if (this.endDateField) {
        defaults[`field_${this.endDateField.id}`] = this.valueForField(
          selection.end,
          this.endDateField
        )
      }
      this.$refs.rowCreateModal.show(defaults)
      selection.view.calendar.unselect()
    },
    openEvent({ event }) {
      const row = event.extendedProps.row
      this.$refs.rowEditModal.show(row.id, clone(row))
      this.$emit('selected-row', row)
    },
    moveEvent(info) {
      return this.updateEventDates(info)
    },
    resizeEvent(info) {
      return this.updateEventDates(info)
    },
    async updateEventDates(info) {
      const row = info.event.extendedProps.row
      const eventEnd =
        info.event.end || new Date(info.event.start.getTime() + HOUR)
      const values = {
        [this.startDateField.id]: this.usesJobSchedule
          ? this.valueForSchedule(info.event.start, eventEnd, info.event.allDay)
          : this.valueForField(info.event.start, this.startDateField),
      }
      const oldValues = {
        [this.startDateField.id]: row[`field_${this.startDateField.id}`],
      }

      if (this.endDateField && info.event.end) {
        values[this.endDateField.id] = this.valueForField(
          info.event.end,
          this.endDateField
        )
        oldValues[this.endDateField.id] = row[`field_${this.endDateField.id}`]
      }

      try {
        await this.$store.dispatch(`${this.storePath}/updateRowValues`, {
          table: this.table,
          view: this.view,
          row,
          fields: this.fields,
          values,
          oldValues,
        })
      } catch (error) {
        info.revert()
        notifyIf(error, 'field')
      }
    },
    async createRow({ row, callback }) {
      try {
        await this.$store.dispatch(`${this.storePath}/createNewRow`, {
          view: this.view,
          table: this.table,
          fields: this.fields,
          values: row,
        })
        callback()
      } catch (error) {
        callback(error)
      }
    },
    async updateValue({ field, row, value, oldValue }) {
      try {
        await this.$store.dispatch(`${this.storePath}/updateRowValue`, {
          table: this.table,
          view: this.view,
          fields: this.fields,
          row,
          field,
          value,
          oldValue,
        })
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    async refreshRow(row) {
      try {
        await this.$store.dispatch(`${this.storePath}/refreshRowFromBackend`, {
          table: this.table,
          row,
        })
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
  },
}
</script>
